import React, { PropsWithChildren } from 'react';
import { DialogContent, DialogTitle, IconButton, InputAdornment, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { FormInputProps } from './form-input-props';
import { DataTable, DataTableColumn } from '../data-table/data-table';
import { ClassConstructor } from 'class-transformer';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '../button/button';
import { useTranslation } from 'react-i18next';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { ResourceClientService } from '@lib/common-sdk';
import { Dialog } from '../dialog/dialog';

export interface FormInputTableSelectProps extends FormInputProps {
  initialId?: string | number | undefined;
  displayFormat: string; // ex '{firstName} {lastName}'
  dialogTitle: string;

  /** The collection the dialog lists, e.g. '/api/resource/communes'. */
  fetchUrl: string;
  modelClass?: ClassConstructor<any>;

  fetchFilters?: any;
  columns: DataTableColumn[];
  selectedValueField?: string; // _id by default
  onRowSelected: (row: any) => void;
  onRowClear?: () => void;
  getFormValues?: () => any;
  initialDisplayValue?: string;
}

export const FormInputTableSelect: React.FC<FormInputTableSelectProps> = (props: PropsWithChildren<FormInputTableSelectProps>) => {
  const [openDialog, setOpenDialog] = React.useState(false);
  const [displayedValue, setDisplayedValue] = React.useState(props.initialDisplayValue || '');
  const [selectedValue, setSelectedValue] = React.useState<string | null>('');
  const { t } = useTranslation();

  function computeFetchFilters() {
    const filters = props.fetchFilters != undefined ? { ...props.fetchFilters } : undefined;
    if (props.getFormValues != null && props.fetchFilters != null) {
      const formValues = props.getFormValues();
      Object.keys(props.fetchFilters).forEach((key) => {
        const paramVal = props.fetchFilters[key];
        if (typeof paramVal === 'string' && paramVal.startsWith('$')) {
          filters[key] = formValues[paramVal.substring(1)];
        } else {
          filters[key] = paramVal;
        }
      });
    }
    return filters;
  }

  const computedFetchFilters = computeFetchFilters();

  const handleSelect = React.useCallback(
    async (row: any, onChange: (...event: any[]) => void, isSetValue?: boolean) => {
      const idFieldName = 'id';
      // Spring Data REST resolved an association from a URI, so picking a row used to put one in the form field.
      // Nothing resolves URIs any more - the commands take the id, and always did on every other picker in the app.
      const selectedValue = props.selectedValueField != null ? row[props.selectedValueField] : row[idFieldName];
      props.onRowSelected(row);
      setDisplayedValue(formatDisplay(props.displayFormat, row));
      setSelectedValue(selectedValue);
      if (isSetValue === true) {
        onChange(props.name, selectedValue);
      } else {
        onChange(selectedValue);
      }
      setOpenDialog(false);
    },
    [props.displayFormat, props.fetchUrl, props.selectedValueField, setDisplayedValue, setOpenDialog, setSelectedValue],
  );

  React.useEffect(() => {
    if (props.initialId != null) {
      if (props.selectedValueField != null && props.selectedValueField !== '_id' && props.selectedValueField !== 'id') {
        const filters: any = {};
        const idFieldName = 'id';
        filters[props.selectedValueField != null ? props.selectedValueField : idFieldName] = props.initialId;
        ResourceClientService.findAll<any>(props.fetchUrl, filters, { modelClass: props.modelClass }).then((results) => {
          if (!results.failed) {
            if (results.response && results.response.length > 0) {
              handleSelect(results.response[0], props.setValue, true);
            }
          }
        });
      } else {
        ResourceClientService.findOne<any>(`${props.fetchUrl}/${props.initialId}`, undefined, { modelClass: props.modelClass }).then((results) => {
          if (!results.failed && results.response != null) {
            handleSelect(results.response, props.setValue, true);
          }
        });
      }
    }
    // Keyed on what there is to preselect, never on the callbacks it invokes. handleSelect closes over
    // props.onRowSelected, which GeneralForm passes as an inline arrow and rebuilds on every render - and it re-renders
    // on every form value change, because it calls watch(). Depending on it re-ran this effect on each of those
    // renders: it re-fetched the initial row, overwrote whatever the user had picked and called setOpenDialog(false),
    // so a pre-filled picker reverted its value and its dialog shut the moment it was opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialId, props.modelClass, props.fetchUrl, props.selectedValueField]);

  function formatDisplay(text: string, args: any) {
    const placeholderRegex = /\{([^{}]+)\}/g;
    return text.replace(placeholderRegex, (match, path) => {
      const keys = path.split('.');
      let value = args;
      for (const key of keys) {
        if (value === undefined || value === null) {
          return match;
        }
        value = value[key];
      }
      return value !== undefined ? value : match;
    });
  }

  async function handleCloseDialog() {
    setOpenDialog(false);
  }

  async function handleInputClick(onChange: (...event: any[]) => void) {
    if (displayedValue !== '') {
      props.onRowSelected(null);
      setDisplayedValue('');
      onChange('');
      setSelectedValue(null);
    } else {
      setOpenDialog(true);
    }
  }

  function processValueToDisplay(displayedValue: string, value: any) {
    if (value === '' && selectedValue !== value) {
      props.onRowSelected(null);
      setDisplayedValue('');
      setSelectedValue(null);
    } else if (value !== '' && selectedValue !== value) {
      setSelectedValue(value);
    }
    return displayedValue;
  }

  return (
    <Controller
      name={props.name}
      control={props.control}
      render={({ field: { onChange, value }, fieldState: { error }, formState }) => (
        <>
          <div data-test-id={`input-${props.name}-wrapper`}>
            <TextField
              inputProps={{ 'data-test-id': `input-${props.name}` }}
              helperText={error ? error.message : ' '}
              error={!!error}
              size='small'
              value={processValueToDisplay(displayedValue, value)}
              onChange={(event) => {
                onChange(selectedValue);
              }}
              onClick={(event) => setOpenDialog(true)}
              onKeyDown={(event) => event.preventDefault()}
              fullWidth
              label={props.label}
              variant={props.variant || 'outlined'}
              margin='normal'
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton data-test-id={`input-${props.name}-clear-btn`} onClick={() => handleInputClick(onChange)}>
                      {displayedValue !== '' ? <ClearIcon /> : <SearchIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                caretColor: 'transparent',
                cursor: 'pointer',
              }}
              disabled={props.isDeactivated}
            />
          </div>
          <Dialog open={openDialog} onClose={() => handleCloseDialog()} maxWidth='xl' fullWidth={true}>
            <DialogTitle>
              {props.dialogTitle}
              <IconButton
                data-test-id={'dialog-close-btn'}
                aria-label='close'
                onClick={handleCloseDialog}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <DataTable
                refreshKey={1}
                columns={props.columns}
                fetchUrl={props.fetchUrl}
                modelClass={props.modelClass}
                fetchFilters={computedFetchFilters}
                rowOptions={[
                  {
                    renderer: (row) => <Button label={t('Common.select')} color='primary' variant='outlined' onClick={async () => handleSelect(row, onChange)} />,
                  },
                ]}
              />
            </DialogContent>
          </Dialog>
        </>
      )}
    />
  );
};
